const reserved = new Map([
	[
		"contains",
		{
			match: `contains\\("([^"]+?)"\\)`,
			replace: {
				start: "*",
				end: "*",
			},
		},
	],
	[
		"startsWith",
		{
			match: `startsWith\\("([^"]+?)"\\)`,
			replace: {
				start: "",
				end: "*",
			},
		},
	],
	[
		"endsWith",
		{
			match: `endsWith\\("([^"]+?)"\\)`,
			replace: {
				start: "*",
				end: "",
			},
		},
	],
]);

const AND_NOT = "AND NOT";
const OR_NOT = "OR NOT";

export const specialAndNotCombinations = [",not", "&not", "&!", "&~", ",!", ",~", "and!", "and~"];
export const specialOrNotCombinations = ["or!", "or~", "|~", "|!", "|not"];

const specialsSeparators = [
	...specialAndNotCombinations.map((separator) => ({ key: `\\${separator}`, replaceWith: AND_NOT })),
	...specialOrNotCombinations.map((separator) => ({ key: `\\${separator}`, replaceWith: OR_NOT })),
];

const specialRules = new Map([
	[
		">",
		{
			next: "=",
			replace: ">=",
		},
	],
	[
		"<",
		{
			next: "=",
			replace: "<=",
		},
	],
]);

const regexRules = [
	{
		match: `^=([a-zA-Z0-9]+)$`,
		prev: "<",
		replace: "<=",
	},
	{
		match: `^=([a-zA-Z0-9]+)$`,
		prev: ">",
		replace: ">=",
	},
];

const normalizeReserved = (expression, matches = [], match, replace) => {
	const { start, end } = replace;
	const regex = new RegExp(match, "i");
	for (const expr of matches) {
		if (regex.test(expr)) {
			let groups = expr.match(regex);
			let text = groups?.[1] || "";
			// Replace the expr on the original expression by the new definition
			if (text) {
				let replacement = `"${start}${text}${end}"`.trim();
				expression = expression.replace(expr, replacement);
			}
		}
	}
	return expression;
};

const correctWords = (expression, keys = []) => {
	if (!expression || !keys.length) return expression;
	for (const key of keys) {
		const { match, replace } = reserved.get(key);
		const regex = new RegExp(match, "gi");
		const matches = expression.match(regex);
		if (matches?.length && replace) {
			// Normalize the expression
			expression = normalizeReserved(expression, matches, match, replace);
		}
	}
	return expression;
};

const processReservedWords = (expression, keys = [], separator) => {
	if (!expression || !keys.length) return expression;
	return expression
		.split(separator)
		.map((slide) => correctWords(slide, keys))
		.join(separator);
};

const reservedCorrections = (expression, splitters = [":"]) => {
	const reservedWords = Array.from(reserved.keys());
	for (const separator of splitters) {
		expression = processReservedWords(expression, reservedWords, separator);
	}
	return expression;
};

const isRecognizedSyntax = (expression) => {};

const getSyntaxCorrection = (text, syntax) => {
	if (!text || !syntax.size) return text;
	let key = text?.toString().toLowerCase();
	let isSyntaxKey = syntax.get(key);
	if (isSyntaxKey) return ` ${key} `;
	return text;
};

const correctInnerParts = (slide = "", syntax, separator = " ") => {
	if (!slide || !syntax.size) return slide;
	let innerParts = slide.split(separator);
	if (innerParts?.length > 1) {
		slide = innerParts.map((item) => getSyntaxCorrection(item, syntax)).join(separator);
	}
	return getSyntaxCorrection(slide, syntax);
};

const correctSyntax = (expression, syntax, separator) => {
	if (!expression || !syntax.size || !separator) return expression;
	return expression
		.split(separator)
		.map((slide) => correctInnerParts(slide, syntax))
		.join(separator);
};

const syntaxCorrections = (expression = "", syntax = new Map(), splitters = [`:`, `["`]) => {
	if (!expression || !syntax.size) return expression;

	// Special cases for join statements who should appear in a different way
	specialsSeparators.forEach((special) => {
		const { key, replaceWith } = special;
		let regex = new RegExp(key, "g");
		expression = expression.replace(regex, replaceWith);
	});

	for (const separator of splitters) {
		expression = correctSyntax(expression, syntax, separator);
	}

	// Replace extra spaces
	expression = expression.replace(/\s+(?=([^"]*"[^"]*")*[^"]*$)/g, " ");
	return expression?.trim();
};

const processRegexRule = (token, rule) => {
	const { match, replace } = rule;
	const regex = new RegExp(match);
	let groups = token.match(regex);
	let expression = groups?.[1] || "";
	return {
		token: expression,
		prev: replace,
	};
};

const getMatchRule = (token, previousToken) => {
	for (const rule of regexRules) {
		let regex = new RegExp(rule.match);
		if (regex.test(token) && rule.prev === previousToken) return rule;
	}
	return null;
};

const checkRegexRules = (tokens = []) => {
	if (!tokens?.length) return tokens;
	for (let i = 1; i < tokens.length - 1; i++) {
		let token = tokens[i];
		let prevToken = tokens[i - 1];
		const rule = getMatchRule(token, prevToken);
		if (rule) {
			const values = processRegexRule(token, rule);
			if (values) {
				tokens[i] = values.token;
				tokens[i - 1] = values.prev;
			}
		}
	}
	return tokens;
};

const correctExtremeRules = (tokens = []) => {
	if (!tokens?.length) return tokens;
	const start = /^"([a-zA-Z0-9]+)$/i;
	const end = /^([a-zA-Z0-9]+)"$/i;

	for (let i = 1; i < tokens.length - 1; i++) {
		const current = tokens[i];
		const previous = tokens[i - 1];

		if (start.test(previous) && end.test(current)) {
			let isCorrectable = !Boolean(previous.includes('""') || current.includes('""'));
			if (isCorrectable) {
				tokens[i - 1] = `${previous} ${current}`;
				tokens[i] = null;
			}
		}
	}
	return tokens.filter((token) => token);
};

const syntaxRules = (tokens) => {
	if (!tokens?.length) return [];
	for (let i = 0; i < tokens.length - 1; i++) {
		let rule = specialRules.get(tokens[i]?.trim());
		let nextToken = tokens[i + 1];
		if (rule && rule?.next === nextToken) {
			tokens[i] = null;
			tokens[i + 1] = rule.replace;
		}
	}
	tokens = tokens.filter((token) => token);
	tokens = checkRegexRules(tokens);
	return correctExtremeRules(tokens);
};

export { isRecognizedSyntax, reservedCorrections, syntaxCorrections, syntaxRules };
